import React, { StrictMode } from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import {
  CloseOutlined,
  SearchOutlined,
  NumberOutlined,
  CalendarOutlined,
  FontSizeOutlined,
  FieldTimeOutlined,
  TagOutlined,
  BarsOutlined,
  DragOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import { Checkbox, theme, Input, Collapse, Tag, Tooltip, Button } from "antd";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  DroppableProps,
} from "react-beautiful-dnd";
import { attributeToTitle } from "src/utils/strings";
import type { CSSProperties } from "react";
import styles from "./TableColumns.module.css";
import { reorder } from "src/utils/collections";
import { Api } from "@vzbl/integrations";
import { unitTypeForCol } from "src/utils/strings";

// Column type definitions
const columnTypeIcons: Record<string, React.ReactNode> = {
  string: <FontSizeOutlined />,
  number: <NumberOutlined />,
  date: <CalendarOutlined />,
  datetime: <FieldTimeOutlined />,
  "string[]": <TagOutlined />,
  default: <BarsOutlined />,
};

interface TableColumn {
  name: string;
  type: string;
}

export interface TableColumnsProps {
  selectedColumns: string[];
  defaultColumns: string[];
  allColumns: string[];
  columnFormulaNames: string[];
  columnInfo: Api.Schema.ColumnInfo;
  onSelectColumn: (column: string) => void;
  onRemoveColumn: (column: string) => void;
  onReorderColumns: (columns: string[]) => void;
}

// StrictMode compatible Droppable
const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

// Get column type from schema info
function getColumnType(
  columnName: string,
  columnInfo: Api.Schema.ColumnInfo
): string {
  if (!columnName || !columnInfo || !Array.isArray(columnInfo)) {
    return "string";
  }
  const { type } = columnInfo.find((c) => c?.key === columnName) || {
    type: "string",
  };
  return type || "string";
}

// Column categories with dynamic categorization using schema types
function categorizeColumns(
  columns: string[],
  columnInfo: Api.Schema.ColumnInfo
): Record<string, TableColumn[]> {
  const categories: Record<string, TableColumn[]> = {
    "Basic Information": [],
    "File Statistics": [],
    Metrics: [],
    Time: [],
    Other: [],
  };

  if (!columns || !Array.isArray(columns)) return categories;

  columns.forEach((col) => {
    if (!col) return;

    const type = getColumnType(col, columnInfo || []);
    const column = { name: col, type };

    if (/name|message|author|description|title/i.test(col)) {
      categories["Basic Information"].push(column);
    } else if (/file|added|deleted|modified/i.test(col)) {
      categories["File Statistics"].push(column);
    } else if (
      /score|count|size|total|number|sum|avg|median|mode|min|max|range|variance|stddev|percentile/i.test(
        col
      )
    ) {
      categories["Metrics"].push(column);
    } else if (/interval|time|date|age/i.test(col)) {
      categories["Time"].push(column);
    } else {
      categories["Other"].push(column);
    }
  });

  // Remove empty categories
  Object.keys(categories).forEach((key) => {
    if (categories[key].length === 0) {
      delete categories[key];
    }
  });

  return categories;
}

export const TableColumns: React.FC<TableColumnsProps> = ({
  selectedColumns = [],
  defaultColumns = [],
  allColumns = [],
  columnFormulaNames = [],
  columnInfo = [],
  onSelectColumn,
  onRemoveColumn,
  onReorderColumns,
}) => {
  const { token } = theme.useToken();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newColumns = reorder(
      selectedColumns,
      result.source.index,
      result.destination.index
    );

    onReorderColumns(newColumns);
  };

  // Filter and categorize available columns based on search
  const availableColumns = useMemo(() => {
    const unselectedColumns = allColumns.filter(
      (col) => !selectedColumns.includes(col)
    );
    if (!searchQuery) return categorizeColumns(unselectedColumns, columnInfo);

    const filtered = unselectedColumns.filter((col) =>
      attributeToTitle(col).toLowerCase().includes(searchQuery.toLowerCase())
    );
    return categorizeColumns(filtered, columnInfo);
  }, [allColumns, selectedColumns, searchQuery, columnInfo]);

  // Handle click outside search to close popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      style={{
        width: "100%",
        paddingLeft: 0,
      }}
    >
      <div
        className="section-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${token.colorBorder}`,
        }}
      >
        <span>Selected Columns</span>
        <span>{selectedColumns.length}</span>
      </div>

      {/* Search Section */}
      <div style={{ padding: "4px 0 12px 0", width: "100%" }}>
        <div className={styles.searchContainer} ref={searchRef}>
          <Input
            placeholder={`Search (${
              allColumns.length - selectedColumns.length
            } available)`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            className={styles.searchInput}
            prefix={<SearchOutlined className={styles.searchIcon} />}
          />

          {/* Search Results Popup */}
          {isSearchFocused && (
            <div className={styles.searchPopup}>
              {Object.entries(availableColumns).map(([category, columns]) => (
                <div key={category}>
                  <div className={styles.categoryHeader}>
                    {category}
                    <span className={styles.categoryCount}>
                      {columns.length}
                    </span>
                  </div>
                  <div className={styles.columnList}>
                    {columns.map((column) => (
                      <div
                        key={column.name}
                        className={styles.columnItem}
                        onClick={() => {
                          onSelectColumn(column.name);
                          setSearchQuery("");
                          setIsSearchFocused(false);
                        }}
                      >
                        {columnTypeIcons[column.type]}
                        <span style={{ marginLeft: 8 }}>
                          {attributeToTitle(column.name)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {Object.keys(availableColumns).length === 0 && (
                <div
                  style={{
                    padding: "12px",
                    color: token.colorTextSecondary,
                    textAlign: "center",
                  }}
                >
                  No matching columns found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Columns Section */}
      <div className={styles.selectedSection}>
        <DragDropContext onDragEnd={onDragEnd}>
          <StrictModeDroppable droppableId="droppable">
            {(droppableProvided: DroppableProvided) => (
              <div
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                className={styles.selectedList}
              >
                {selectedColumns.map((column, index) => (
                  <Draggable key={column} draggableId={column} index={index}>
                    {(draggableProvided, snapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        className={`${styles.selectedColumnItem} ${
                          snapshot.isDragging ? styles.dragging : ""
                        }`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                          }}
                        >
                          <HolderOutlined
                            style={{
                              color: "var(--color-text-tertiary)",
                              padding: "4px 0px",
                            }}
                          />
                          <span style={{ flex: 1 }}>
                            {attributeToTitle(column)}
                          </span>

                          {/* Column Type Icon */}
                          <div
                            style={{
                              color: token.colorTextQuaternary,
                              fontSize: 14,
                              marginRight: 4,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            {columnTypeIcons[
                              getColumnType(column, columnInfo)
                            ] || columnTypeIcons.default}
                          </div>

                          <CloseOutlined
                            className={styles.closeButton}
                            onClick={() => onRemoveColumn(column)}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      </div>
    </div>
  );
};
